import { authService } from './authService';

export interface APIRequest {
  query: string;
  sessionId?: string;
}

/**
 * Server-verified details of a high-risk action awaiting Human-In-The-Loop
 * confirmation. Built by the Query Resolver from registry-resolved parameters
 * (not the agent's restated text) — see hitl.build_confirmation_response.
 */
export interface ConfirmationDetails {
  action: string;
  function: string;
  parameters: Record<string, string>;
  target?: string;
  account?: string;
  isProduction: boolean;
  prompt: string;
  expiresAt: string;
}

export interface APIResponse {
  // Optional: a confirmation-required response carries no free-text answer.
  response?: string;
  timestamp: string;
  sessionId?: string;
  userAlias?: string;
  downloadUrl?: string;
  // HITL: present when a gated tool paused for human confirmation.
  requiresConfirmation?: boolean;
  confirmationToken?: string;
  confirmation?: ConfirmationDetails;
}

export type ConfirmationDecision = 'approve' | 'deny';

export type BugReportCategory = 'bug' | 'feature_request' | 'other';
export type BugReportSeverity = 'low' | 'medium' | 'high';

/** A user-submitted bug/issue report (spec ordis-bug-report). */
export interface BugReportInput {
  title: string;
  description: string;
  category: BugReportCategory;
  severity?: BugReportSeverity;
}

export interface BugReportResponse {
  report_id: string;
}

export type JobStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'UNKNOWN';

export interface JobStatusResponse {
  job_id: string;
  status: JobStatus;
  operation_type?: string;
  server_name?: string;
  created_at?: string;
  updated_at?: string;
  elapsed_seconds?: number;
  result?: string;
  error?: string;
}

export class JobStatusNotFoundError extends Error {
  constructor(jobId: string) {
    super(`No job found with ID '${jobId}'.`);
    this.name = 'JobStatusNotFoundError';
  }
}

/**
 * Error thrown for any non-OK HTTP response (other than 401, which triggers
 * re-login, and 404 from the job-status endpoint, which is its own type).
 * Carries both the HTTP status and the parsed body's `error` field so the
 * UI can surface backend-authored messages (RBAC denials, prod-access
 * denials, rate limit text) instead of collapsing every failure into a
 * single string.
 */
export class APIRequestError extends Error {
  status: number;
  serverMessage?: string;
  constructor(status: number, serverMessage?: string) {
    super(serverMessage || `HTTP error! status: ${status}`);
    this.name = 'APIRequestError';
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

async function readErrorMessage(response: Response): Promise<string | undefined> {
  try {
    const errBody = await response.json();
    if (errBody && typeof errBody.error === 'string') {
      return errBody.error;
    }
  } catch {
    // Body wasn't JSON or was empty — fall through with no serverMessage.
  }
  return undefined;
}

// API endpoints — uses same domain via CloudFront path-based routing
const API_ENDPOINTS = {
  beta: '',
  gamma: '',
};

class OrdisAPIService {
  private apiEndpoint: string | undefined;
  private initPromise: Promise<void> | undefined;
  private sessionId: string | undefined;
  private ticketId: string | undefined;

  private async initialize(): Promise<void> {
    if (this.apiEndpoint !== undefined) return;

    const currentUrl = window.location.hostname;
    console.log('Initializing API Service with hostname:', currentUrl);

    if (currentUrl.includes('gamma')) {
      this.apiEndpoint = API_ENDPOINTS.gamma;
    } else {
      this.apiEndpoint = API_ENDPOINTS.beta;
    }

    this.sessionId = `session-${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }

  private async ensureInitialized(): Promise<void> {
    if (this.apiEndpoint !== undefined) return;
    if (!this.initPromise) {
      this.initPromise = this.initialize();
    }
    await this.initPromise;
  }

  async resolveQuery(request: APIRequest): Promise<APIResponse> {
    await this.ensureInitialized();

    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isLocalhost) {
      return {
        response:
          'This is a test response from localhost. When deployed, this will connect to the actual Bedrock API.',
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId || undefined,
      };
    }

    const requestWithSession = {
      ...request,
      sessionId: this.sessionId,
      ...(this.ticketId ? { ticketId: this.ticketId } : {}),
    };

    // Get access token from auth service
    const accessToken = authService.getAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    try {
      const response = await fetch(`${this.apiEndpoint}/query`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestWithSession),
      });

      if (response.status === 401) {
        // Token expired — trigger re-login
        authService.login();
        throw new Error('Authentication expired, redirecting to login');
      }

      if (!response.ok) {
        const serverMessage = await readErrorMessage(response);
        throw new APIRequestError(response.status, serverMessage);
      }

      const data = await response.json();

      // Store ticketId for subsequent requests in the same session
      if (data.ticketId) {
        this.ticketId = data.ticketId;
      }

      return data;
    } catch (error) {
      // A TypeError from fetch means the request never received an HTTP
      // response: the ALB 302'd us to the Cognito Hosted UI because the
      // AWSELBAuthSessionCookie is missing or expired, and the browser
      // blocked the cross-origin redirect. Re-run the ALB bootstrap to
      // re-mint the cookie. The freshness guard prevents a redirect loop —
      // if we bootstrapped recently and it's still failing, surface the
      // error instead of navigating again.
      if (error instanceof TypeError && !authService.isAlbSessionFresh()) {
        authService.bootstrapAlbSession();
      }
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * Submit a human Approve/Deny for a pending high-risk action (HITL).
   *
   * Posts the single-use ``confirmationToken`` + ``decision`` back to
   * ``/query`` on the same session. The server resolves the token
   * (single-use, default-deny), and on approval resumes the paused Bedrock
   * Agent so the gated tool finally executes. Returns the agent's resulting
   * turn (or the cancellation message on deny).
   */
  async confirmAction(token: string, decision: ConfirmationDecision): Promise<APIResponse> {
    await this.ensureInitialized();

    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      return {
        response:
          decision === 'approve'
            ? 'Localhost test — action approved (no real execution).'
            : 'Localhost test — action cancelled.',
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
      };
    }

    const accessToken = authService.getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${this.apiEndpoint}/query`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        confirmationToken: token,
        decision,
        sessionId: this.sessionId,
      }),
    });

    if (response.status === 401) {
      authService.login();
      throw new Error('Authentication expired, redirecting to login');
    }
    if (!response.ok) {
      const serverMessage = await readErrorMessage(response);
      throw new APIRequestError(response.status, serverMessage);
    }

    return (await response.json()) as APIResponse;
  }

  resetSession(): void {
    this.sessionId = `session-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    this.ticketId = undefined;
  }

  /**
   * Look up the current state of an async background job (e.g. the MSSQL
   * driver install kicked off by setup_odbc_connection). Returns the
   * worker's most recent record from the async jobs table. Backed by the
   * Query Resolver's read-only /job-status route, which enforces
   * ownership against the caller's Midway alias — a wrong-owner lookup
   * is indistinguishable from a missing record (404).
   */
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    await this.ensureInitialized();

    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      return {
        job_id: jobId,
        status: 'COMPLETED',
        result: 'Localhost test response — no real polling.',
        elapsed_seconds: 0,
      };
    }

    const accessToken = authService.getAccessToken();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const url = `${this.apiEndpoint}/job-status?job_id=${encodeURIComponent(jobId)}`;
    const response = await fetch(url, { method: 'GET', headers });

    if (response.status === 401) {
      authService.login();
      throw new Error('Authentication expired, redirecting to login');
    }
    if (response.status === 404) {
      throw new JobStatusNotFoundError(jobId);
    }
    if (!response.ok) {
      const serverMessage = await readErrorMessage(response);
      throw new APIRequestError(response.status, serverMessage);
    }

    return (await response.json()) as JobStatusResponse;
  }

  /**
   * Submit a bug/issue report (spec ordis-bug-report).
   *
   * POSTs the form fields to ``/report-bug`` on the same origin. The UI
   * attaches non-PII context (sessionId, pageUrl, userAgent); the server
   * sets the reporter identity from the ALB-verified Midway alias — the
   * body cannot spoof it. Mirrors ``resolveQuery`` for auth/error handling.
   */
  async reportBug(input: BugReportInput): Promise<BugReportResponse> {
    await this.ensureInitialized();

    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      return { report_id: `local-${Date.now()}` };
    }

    const payload = {
      title: input.title,
      description: input.description,
      category: input.category,
      severity: input.severity ?? 'medium',
      sessionId: this.sessionId,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
    };

    const accessToken = authService.getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${this.apiEndpoint}/report-bug`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      authService.login();
      throw new Error('Authentication expired, redirecting to login');
    }
    if (!response.ok) {
      const serverMessage = await readErrorMessage(response);
      throw new APIRequestError(response.status, serverMessage);
    }

    return (await response.json()) as BugReportResponse;
  }
}

export const ordisAPIService = new OrdisAPIService();
